/**
 * MOB Recovery Equipment - Hierarchical WBC3-Verified Implementation
 * Replaces simple MOBRecoverySection with complete RA-verified requirements
 * Implements universal baseline + vessel-specific additive requirements
 */

import React from 'react';
import { AlertTriangle, Info, Ship, Shield, Anchor } from 'lucide-react';
import { Switch } from '../../../../../catalyst/checkbox';
import WizardSection from '../../WizardSection';
import { getMOBRequirements, validateMOBCompliance } from '../utils/safetyEquipmentLogic';

const MOBRecoveryHierarchical = ({ 
  draftConfig, 
  updateConfig, 
  showRequiredWarning = false 
}) => {
  // Get complete MOB requirements for this vessel
  const mobRequirements = getMOBRequirements(draftConfig);
  
  // Check if this vessel needs MOB equipment at all
  const needsMOBEquipment = draftConfig.operatingStatus === 'seagoing';
  
  if (!needsMOBEquipment) {
    return null; // Don't show for inland vessels
  }

  // Validate current selections
  const selectedMOBEquipment = draftConfig.mobEquipment || [];
  const validation = validateMOBCompliance(draftConfig, selectedMOBEquipment);

  // Group requirements by category for better display
  const requirementsByCategory = mobRequirements.reduce((groups, req) => {
    if (!groups[req.category]) {
      groups[req.category] = [];
    }
    groups[req.category].push(req);
    return groups;
  }, {});

  // Handle equipment selection changes
  const handleEquipmentChange = (equipmentId, checked) => {
    const currentEquipment = draftConfig.mobEquipment || [];
    const newEquipment = checked 
      ? [...currentEquipment, equipmentId]
      : currentEquipment.filter(e => e !== equipmentId);
    
    updateConfig({ mobEquipment: newEquipment });
  };

  // Category icons and styling
  const getCategoryInfo = (category) => {
    switch (category) {
      case 'universal':
        return { icon: Ship, color: 'blue', label: 'Universal Requirement' };
      case 'pilot':
      case 'pilot-conditional':
        return { icon: Anchor, color: 'purple', label: 'Pilot Boat Requirements' };
      case 'police':
      case 'police-conditional':
        return { icon: Shield, color: 'red', label: 'Police Boat Requirements' };
      default:
        return { icon: Info, color: 'gray', label: 'Additional Requirements' };
    }
  };

  return (
    <WizardSection.Card data-testid="mob-recovery-hierarchical">
      <WizardSection.Header>
        <div className="flex items-center gap-2">
          MOB Recovery Equipment - Complete Requirements
          {!validation.isValid && (
            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {validation.errors.length} Missing
            </span>
          )}
        </div>
      </WizardSection.Header>
      
      {/* Requirements Summary */}
      <WizardSection.Callout variant="regulatory" title="WBC3 MOB Recovery Requirements">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p><strong>Hierarchical Requirements System:</strong> Your vessel requires {validation.requiredCount} MOB equipment types based on vessel type and configuration.</p>
            <p className="mt-2">Progress: {validation.selectedCount} of {validation.requiredCount} requirements met</p>
          </div>
        </div>
      </WizardSection.Callout>

      {/* Validation Errors */}
      {!validation.isValid && (
        <WizardSection.Callout variant="error">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-800">
              <p><strong>Missing Required Equipment:</strong></p>
              <ul className="mt-2 space-y-1">
                {validation.errors.map((error, index) => (
                  <li key={index}>• {error.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </WizardSection.Callout>
      )}
      
      {/* Equipment Selection by Category */}
      <div className="space-y-6 mt-4">
        {Object.entries(requirementsByCategory).map(([category, requirements]) => {
          const categoryInfo = getCategoryInfo(category);
          const CategoryIcon = categoryInfo.icon;
          
          return (
            <div key={category} className="border rounded-lg p-4">
              {/* Category Header */}
              <div className="flex items-center gap-2 mb-3">
                <CategoryIcon className={`h-5 w-5 text-${categoryInfo.color}-600`} />
                <h4 className={`font-medium text-${categoryInfo.color}-900`}>
                  {categoryInfo.label}
                </h4>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                  {requirements.length} requirement{requirements.length > 1 ? 's' : ''}
                </span>
              </div>
              
              {/* Equipment Items */}
              <div className="space-y-3">
                {/* Group requirements by choice groups */}
                {(() => {
                  const choiceGroups = {};
                  const individualRequirements = [];
                  
                  requirements.forEach(req => {
                    if (req.choiceGroup) {
                      if (!choiceGroups[req.choiceGroup]) {
                        choiceGroups[req.choiceGroup] = [];
                      }
                      choiceGroups[req.choiceGroup].push(req);
                    } else {
                      individualRequirements.push(req);
                    }
                  });
                  
                  return (
                    <>
                      {/* Render individual requirements */}
                      {individualRequirements.map(requirement => {
                        const isSelected = selectedMOBEquipment.includes(requirement.equipmentId);
                        const isMissing = !isSelected && requirement.status === 'MANDATORY';
                        
                        return (
                          <label 
                            key={requirement.equipmentId}
                            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                              isMissing && showRequiredWarning
                                ? 'border-red-300 bg-red-50 hover:bg-red-100'
                                : isSelected
                                  ? 'border-green-300 bg-green-50'
                                  : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <Switch
                              checked={isSelected}
                              onChange={(checked) => handleEquipmentChange(requirement.equipmentId, checked)}
                              className="mt-0.5"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {requirement.name}
                                </span>
                                {requirement.status === 'MANDATORY' && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded">
                                    MANDATORY
                                  </span>
                                )}
                                {requirement.status === 'OPTIONAL' && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                    OPTIONAL
                                  </span>
                                )}
                                {isMissing && showRequiredWarning && (
                                  <AlertTriangle className="h-3 w-3 text-red-500" />
                                )}
                              </div>
                              
                              {requirement.description && (
                                <p className="text-xs text-gray-600 mt-1">
                                  <strong>Specification:</strong> {requirement.description}
                                </p>
                              )}
                              
                              <p className="text-xs text-gray-700 mt-1">
                                <strong>Requirement:</strong> {requirement.reason}
                              </p>
                              
                              <p className="text-xs text-cyan-600 mt-1 font-medium">
                                {requirement.reference}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                      
                      {/* Render choice groups */}
                      {Object.entries(choiceGroups).map(([groupName, groupRequirements]) => {
                        const selectedInGroup = groupRequirements.filter(req => 
                          selectedMOBEquipment.includes(req.equipmentId)
                        );
                        const noChoiceMade = selectedInGroup.length === 0;
                        
                        return (
                          <div 
                            key={groupName}
                            className={`border rounded-lg p-4 ${
                              noChoiceMade && showRequiredWarning
                                ? 'border-red-300 bg-red-50'
                                : 'border-orange-200 bg-orange-50'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-4">
                              <h5 className="text-sm font-medium text-gray-900">
                                Pilot Recovery Equipment (WBC3 14.7.1)
                              </h5>
                              <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-medium rounded">
                                AT LEAST ONE REQUIRED
                              </span>
                              {noChoiceMade && showRequiredWarning && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" />
                                  Selection Required
                                </span>
                              )}
                            </div>
                            
                            <p className="text-xs text-gray-600 mb-3">
                              <strong>WBC3 Requirement:</strong> "Transom steps and/or ladder or equivalent side ladder or scrambling net + propeller guard". Select one or more options that fulfill this requirement.
                            </p>
                            
                            <div className="grid grid-cols-1 gap-2">
                              {groupRequirements.map(requirement => {
                                const isSelected = selectedMOBEquipment.includes(requirement.equipmentId);
                                
                                return (
                                  <label 
                                    key={requirement.equipmentId}
                                    className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors ${
                                      isSelected
                                        ? 'border-orange-400 bg-orange-100'
                                        : 'border-gray-200 bg-white hover:bg-gray-50'
                                    }`}
                                  >
                                    <Switch
                                      checked={isSelected}
                                      onChange={(checked) => handleEquipmentChange(requirement.equipmentId, checked)}
                                      className="mt-0.5"
                                    />
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">
                                          {requirement.name}
                                        </span>
                                      </div>
                                      
                                      <p className="text-xs text-gray-600 mt-1">
                                        {requirement.description}
                                      </p>
                                      
                                      <p className="text-xs text-orange-600 mt-1 font-medium">
                                        {requirement.reference}
                                      </p>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Conditional Configuration Prompts */}
      {(draftConfig.vesselType === 'pilot' || draftConfig.vesselType === 'police') && (
        <WizardSection.Callout variant="info" title="Additional Configuration">
          <div className="text-sm space-y-2">
            {draftConfig.vesselType === 'pilot' && (
              <p>
                <strong>Pilot Boats:</strong> If your vessel does not have proper guardrails or bulwarks, additional fall prevention equipment is required (WBC3 22.2.3). This can be configured in vessel settings.
              </p>
            )}
            {draftConfig.vesselType === 'police' && (
              <p>
                <strong>Police Boats:</strong> If your vessel has low freeboard that makes person recovery difficult, additional recovery systems are required (WBC3 Annex 3 20.4.2). This can be configured in vessel settings.
              </p>
            )}
          </div>
        </WizardSection.Callout>
      )}

      {/* Equipment Selection Guidance */}
      {mobRequirements.length > 0 && (
        <WizardSection.Callout variant="info" title="Implementation Guidance">
          <ul className="text-sm space-y-1">
            <li>• <strong>Universal Equipment:</strong> All seagoing vessels must demonstrate practical MOB recovery capability</li>
            <li>• <strong>Pilot Boats:</strong> Enhanced requirements for person transfer operations and night/adverse weather recovery</li>
            <li>• <strong>Police Boats:</strong> Enhanced requirements for enforcement scenarios including unconscious person recovery</li>
            <li>• <strong>Compliance:</strong> All equipment must be demonstrated as effective during annual safety drills</li>
          </ul>
        </WizardSection.Callout>
      )}
    </WizardSection.Card>
  );
};

export default MOBRecoveryHierarchical;